import { RawDraftContentState } from "draft-js";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreateFaqMutation } from "entities/faqs/mutations/useCreateFaqMutation";
import { useUpdateFaqMutation } from "entities/faqs/mutations/useUpdateFaqMutation";
import { useFaqDetailsQuery } from "entities/faqs/queries/useFaqDetailsQuery";
import { FaqDataToCreate, FaqGroupStatus } from "entities/faqs/types";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { createContext } from "shared/utils/context";
import { useFaqs } from "../../../context";
import { FaqDetailsTabs, FaqDetailsType } from "./types";

const [Provider, useFaqDetails] = createContext<FaqDetailsType>({
  name: "FaqDetailsContext",
  hookName: "useFaqDetails",
  providerName: "FaqDetailsProvider",
});
type FaqAnswer = { editorContent?: RawDraftContentState } | string;

const FaqDetailsProvider: FC<PropsWithChildren> = (props) => {
  const { faqDetailsDrawer, faqGroupId } = useFaqs();
  const [selectedTab, setSelectedTab] = useState(FaqDetailsTabs.General);
  const [editorContent, setEditorContent] = useState<RawDraftContentState>();
  const questionInput: InputHookResult = useInputState("question", "");
  const questionValue = questionInput?.input.value;

  const { allowed: hasReadAccess } = useRolePermission("games:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "games:translations:read",
  );
  const {
    data: faqData = null,
    isLoading,
    isRefetching,
  } = useFaqDetailsQuery(faqGroupId, faqDetailsDrawer.drawerDetails ?? "");

  const [translations, setTranslations] = useState<
    Record<
      string,
      {
        Question?: string;
        Answer?:
          | {
              editorContent?: RawDraftContentState;
            }
          | string;
      }
    >
  >();

  const { mutate: updateFaqDetails, isPending: isSubmittingFaq } =
    useUpdateFaqMutation();
  const { mutate: createFaqDetails, isPending } = useCreateFaqMutation();

  useEffect(() => {
    if (!isLoading && !isRefetching && faqData) {
      questionInput.setValue(faqData.question);
      setTranslations(faqData.translations);
      setEditorContent(faqData.answer.editorContent || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isRefetching]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(FaqDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, faqData?.id]);

  const handleDrawerClose = () => {
    questionInput.setValue("");
    setEditorContent(undefined);
    setTranslations(undefined);
    setSelectedTab(FaqDetailsTabs.General);
    faqDetailsDrawer.closeDrawer();
  };

  const handleEditorChange = (content: RawDraftContentState) => {
    setEditorContent(content);
  };

  const handleTranslationEditorChange = useCallback(
    (locale: string, content: RawDraftContentState) => {
      if (!content?.blocks?.length || !content.blocks[0]?.text) {
        return;
      }

      setTranslations((prev) => {
        const isAnswerObject = (
          answer: FaqAnswer,
        ): answer is { editorContent?: RawDraftContentState } => {
          return (
            typeof answer === "object" &&
            answer !== null &&
            (!("editorContent" in answer) ||
              answer.editorContent === undefined ||
              "blocks" in answer.editorContent)
          );
        };

        const prevAnswer = prev?.[locale]?.Answer;
        const prevContent =
          prevAnswer && isAnswerObject(prevAnswer)
            ? prevAnswer.editorContent
            : undefined;

        if (
          prevContent &&
          JSON.stringify(prevContent) === JSON.stringify(content)
        ) {
          return prev;
        }

        return {
          ...prev,
          [locale]: {
            ...prev?.[locale],
            Answer: {
              ...(prevAnswer && isAnswerObject(prevAnswer) ? prevAnswer : {}),
              editorContent: content,
            },
          },
        };
      });
    },
    [],
  );

  const disabled = useMemo(
    () => !questionValue || !editorContent?.blocks.length,
    [editorContent?.blocks.length, questionValue],
  );

  const onUpdateFaqDetails = async (status: FaqGroupStatus) => {
    if (faqData && faqGroupId) {
      return updateFaqDetails({
        faqData: {
          ...faqData,
          question: questionValue,
          answer: {
            editorContent,
          },
          status,
          translations: translations ?? {},
        },
        faqId: faqData.id,
        faqGroupId,
      });
    }
  };

  const onCreateFaqGroup = async (status: FaqGroupStatus) => {
    const dataToSubmit: FaqDataToCreate = {
      question: questionValue,
      answer: { editorContent },
      order: 0,
      status,
      translations: translations ?? {},
    };

    return createFaqDetails({ data: dataToSubmit, faqGroupId });
  };

  const onSubmit = async (status: FaqGroupStatus) => {
    if (disabled) {
      return;
    }

    try {
      if (faqData?.id) {
        await onUpdateFaqDetails(status);
      } else {
        await onCreateFaqGroup(status);
      }

      handleDrawerClose();
    } catch (error) {
      console.error("Error submitting faq details:", error);
    }
  };

  const handleSaveDraft = () => onSubmit(FaqGroupStatus.Draft);
  const handleSaveActive = () => onSubmit(FaqGroupStatus.Active);

  const handleSaveTranslations = async () => {
    if (disabled) {
      return;
    }

    if (!faqData?.id) {
      return;
    }

    if (faqData && faqGroupId) {
      await updateFaqDetails({
        faqData: {
          translations: translations ?? {},
        },
        faqId: faqData.id,
        faqGroupId,
      });
    }

    handleDrawerClose();
  };

  const value = {
    isLoading,
    disabled,
    isSubmitting: isPending || isSubmittingFaq,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    handleDrawerClose,
    faqData,
    questionInput,
    handleSaveDraft,
    handleSaveActive,
    handleSaveTranslations,
    editorContent: faqData?.answer.editorContent || null,
    handleEditorChange,
    handleTranslationEditorChange,
  };

  return <Provider value={value}>{props.children}</Provider>;
};
export { FaqDetailsProvider, useFaqDetails };
